import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import PlatypusLogo from "../components/PlatypusLogo";
import AccessNotice from "../components/AccessNotice";
import { usersApi } from "../services/api";

const MEMORY_TABS = [
  { id: "basics", label: "Basics", icon: "bi-person" },
  { id: "career", label: "Career", icon: "bi-briefcase" },
  { id: "highlights", label: "Highlights", icon: "bi-trophy" },
  { id: "skills", label: "Skills", icon: "bi-stars" },
  { id: "answers", label: "Answers", icon: "bi-chat-left-quote" },
];

const SECTIONS = [
  {
    tab: "basics",
    title: "Links",
    hint: "Recruiters look at these first. Platypus also puts them on cover letters and emails.",
    fields: [
      {
        key: "linkedin",
        label: "LinkedIn",
        placeholder: "https://linkedin.com/in/…",
        col: 12,
      },
      {
        key: "github",
        label: "GitHub",
        placeholder: "https://github.com/…",
        col: 6,
      },
      {
        key: "portfolio",
        label: "Portfolio / website",
        placeholder: "https://",
        col: 6,
      },
    ],
  },
  {
    tab: "basics",
    title: "Common questions",
    hint: "Answers to questions most applications ask.",
    fields: [
      {
        key: "workAuthorization",
        label: "Work authorization",
        options: [
          "Authorized to work in the US",
          "Not authorized to work in the US",
        ],
        col: 6,
      },
      {
        key: "needsSponsorship",
        label: "Need visa sponsorship?",
        options: ["No", "Yes", "In the future"],
        col: 6,
      },
      {
        key: "salaryExpectation",
        label: "Salary expectation",
        placeholder: "e.g. $120,000",
        col: 6,
      },
      {
        key: "startDate",
        label: "Earliest start date",
        placeholder: "e.g. Two weeks notice",
        col: 6,
      },
    ],
  },
  {
    tab: "basics",
    title: "Standard answers",
    hint: 'Set these once and Plateo answers them on every application. Anything left as "Ask me" is outlined on the form for you.',
    fields: [
      {
        key: "address",
        label: "Street address",
        placeholder: "123 Main St, Buffalo, NY 14201",
        col: 12,
      },
      {
        key: "citizenship",
        label: "Citizenship",
        options: [
          "",
          "U.S. citizen",
          "U.S. permanent resident (green card)",
          "Not a U.S. citizen or permanent resident",
          "Decline to answer",
        ],
        col: 6,
      },
      {
        key: "clearance",
        label: "Security clearance",
        options: ["", "None", "Secret", "Top Secret", "TS/SCI"],
        col: 6,
      },
      {
        key: "relocate",
        label: "Willing to relocate?",
        options: ["", "Yes", "No"],
        col: 4,
      },
      {
        key: "onsite",
        label: "Open to on-site or hybrid?",
        options: ["", "Yes", "No"],
        col: 4,
      },
      {
        key: "nonCompete",
        label: "Bound by a non-compete?",
        options: ["", "No", "Yes"],
        col: 4,
      },
      {
        key: "howHeard",
        label: "How did you hear about us?",
        placeholder: "Company website",
        col: 12,
      },
    ],
  },
  {
    tab: "basics",
    title: "Voluntary self-identification",
    hint: 'U.S. employers ask these on most applications, and they\'re optional: leave them as "Ask me" or choose "Decline to answer". Plateo only uses them to answer these questions when an application asks, and never sends them to AI.',
    fields: [
      {
        key: "gender",
        label: "Gender",
        options: ["", "Male", "Female", "Non-binary", "Decline to answer"],
        col: 6,
      },
      {
        key: "hispanic",
        label: "Hispanic or Latino?",
        options: ["", "No", "Yes", "Decline to answer"],
        col: 6,
      },
      {
        key: "ethnicity",
        label: "Race",
        options: [
          "",
          "Asian",
          "East Asian",
          "South Asian",
          "Southeast Asian",
          "Black or African American",
          "White",
          "Hispanic or Latino",
          "Native Hawaiian or Other Pacific Islander",
          "American Indian or Alaska Native",
          "Two or more races",
          "Decline to answer",
        ],
        col: 6,
      },
      {
        key: "veteran",
        label: "Veteran status",
        options: [
          "",
          "I am not a protected veteran",
          "I am a protected veteran",
          "Decline to answer",
        ],
        col: 6,
      },
      {
        key: "disability",
        label: "Disability",
        options: [
          "",
          "No, I don't have a disability",
          "Yes, I have a disability",
          "Decline to answer",
        ],
        col: 6,
      },
    ],
  },
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 60 }, (_, i) => String(THIS_YEAR + 5 - i));

const DEGREE_OPTIONS = [
  "High school diploma",
  "GED",
  "Associate of Arts (AA)",
  "Associate of Science (AS)",
  "Associate of Applied Science (AAS)",
  "Bachelor of Arts (BA)",
  "Bachelor of Science (BS)",
  "Bachelor of Engineering (BEng)",
  "Bachelor of Fine Arts (BFA)",
  "Bachelor of Business Administration (BBA)",
  "Bachelor's degree",
  "Master of Arts (MA)",
  "Master of Science (MS)",
  "Master of Engineering (MEng)",
  "Master of Business Administration (MBA)",
  "Master of Fine Arts (MFA)",
  "Master's degree",
  "Doctor of Philosophy (PhD)",
  "Doctor of Medicine (MD)",
  "Juris Doctor (JD)",
  "Doctor of Education (EdD)",
  "Professional certificate",
  "Bootcamp / coding school",
  "Other",
];

const PROFICIENCY_OPTIONS = [
  "Beginner",
  "Intermediate",
  "Advanced",
  "Expert",
  "Native / bilingual",
];

const parseMonthYear = (value) => {
  const m = String(value || "")
    .trim()
    .match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (m && MONTHS.includes(m[1])) return { month: m[1], year: m[2] };
  const y = String(value || "")
    .trim()
    .match(/^(\d{4})$/);
  if (y) return { month: "", year: y[1] };
  return { month: "", year: "" };
};

const formatMonthYear = (month, year) => {
  if (!year) return "";
  return month ? `${month} ${year}` : year;
};

const isPresent = (value) =>
  /^present|current|now|ongoing$/i.test(String(value || "").trim());

const splitDegreeTitle = (title) => {
  const t = String(title || "").trim();
  if (!t) return { degree: "", field: "", other: "" };
  if (DEGREE_OPTIONS.includes(t)) return { degree: t, field: "", other: "" };
  const matched = DEGREE_OPTIONS.filter((d) => d !== "Other").find(
    (d) => t === d || t.startsWith(`${d} in `) || t.startsWith(`${d}, `),
  );
  if (matched) {
    const rest = t
      .slice(matched.length)
      .replace(/^(?:\s+in\s+|\s*,\s*)/i, "")
      .trim();
    return { degree: matched, field: rest, other: "" };
  }
  return { degree: "Other", field: "", other: t };
};

const joinDegreeTitle = (degree, field, other) => {
  // Keep a literal "Other" until they type a custom qualification, so the select stays on Other.
  if (degree === "Other") return String(other || "").trim() || "Other";
  const d = String(degree || "").trim();
  const f = String(field || "").trim();
  if (!d) return f;
  return f ? `${d} in ${f}` : d;
};

const MEMORY_KINDS = [
  {
    tab: "career",
    kind: "experience",
    title: "Experiences",
    hint: "Jobs, internships, research, volunteering.",
    addLabel: "Add experience",
    titleLabel: "Role / title",
    titlePlaceholder: "e.g. Software Engineering Intern",
    orgLabel: "Company, school or organization",
    orgPlaceholder: "e.g. Acme Corp",
    dates: "range",
    dateMode: "monthYear",
    startLabel: "Start",
    endLabel: "End",
    currentLabel: "Current position",
    showLocation: true,
  },
  {
    tab: "career",
    kind: "education",
    title: "Education",
    hint: "Degrees, schools, and when you finished (or will).",
    addLabel: "Add education",
    titleLabel: "Degree / qualification",
    titleSelect: DEGREE_OPTIONS,
    fieldOfStudy: true,
    orgLabel: "School / university",
    orgPlaceholder: "e.g. University at Buffalo",
    dates: "range",
    dateMode: "monthYear",
    startLabel: "Start",
    endLabel: "Graduation (or expected)",
    showLocation: true,
  },
  {
    tab: "career",
    kind: "project",
    title: "Projects",
    hint: "Personal, school or open-source projects.",
    addLabel: "Add project",
    titleLabel: "Project name",
    titlePlaceholder: "e.g. Plateo job tracker",
    orgLabel: "Skills / tech stack",
    orgPlaceholder: "e.g. React, Node.js, MongoDB",
    dates: "range",
    dateMode: "monthYear",
    startLabel: "Start",
    endLabel: "End",
    currentLabel: "Still working on this",
    showLink: true,
    showLocation: true,
  },
  {
    tab: "highlights",
    kind: "award",
    title: "Awards",
    hint: "Honors, scholarships, hackathon wins.",
    addLabel: "Add award",
    titleLabel: "Award name",
    titlePlaceholder: "e.g. Dean's List, Best Hack",
    orgLabel: "Given by",
    orgPlaceholder: "e.g. University at Buffalo",
    dates: "single",
    dateMode: "monthYear",
    startLabel: "Date received",
  },
  {
    tab: "highlights",
    kind: "publication",
    title: "Publications",
    hint: "Papers, articles, posters and talks.",
    addLabel: "Add publication",
    titleLabel: "Publication / talk title",
    titlePlaceholder: "e.g. A study of …",
    orgLabel: "Venue / publisher (optional)",
    orgPlaceholder: "e.g. NeurIPS 2025, Medium",
    dates: "single",
    dateMode: "monthYear",
    startLabel: "Published / presented",
    showLink: true,
  },
  {
    tab: "highlights",
    kind: "patent",
    title: "Patents",
    hint: "Patents you hold or are named on.",
    addLabel: "Add patent",
    titleLabel: "Patent title",
    titlePlaceholder: "e.g. System for …",
    orgLabel: "Patent number / office (optional)",
    orgPlaceholder: "e.g. US 12,345,678",
    dates: "single",
    dateMode: "monthYear",
    startLabel: "Filed / granted",
    showLink: true,
  },
  {
    tab: "highlights",
    kind: "certification",
    title: "Certifications & licences",
    hint: "Certifications and licences, with expiry dates.",
    addLabel: "Add certification",
    titleLabel: "Certification name",
    titlePlaceholder: "e.g. AWS Certified Cloud Practitioner",
    orgLabel: "Issuer",
    orgPlaceholder: "e.g. Amazon Web Services",
    dates: "range",
    dateMode: "monthYear",
    startLabel: "Issued",
    endLabel: "Expires (optional)",
    showLink: true,
  },
  {
    tab: "skills",
    kind: "skill",
    title: "Skills",
    hint: "Tools, languages and frameworks - one compact row each.",
    addLabel: "Add skill",
    titleLabel: "Skill",
    titlePlaceholder: "e.g. React",
    orgLabel: "Level",
    orgSelect: PROFICIENCY_OPTIONS,
    dates: "none",
    hideDescription: true,
    compact: true,
  },
  {
    tab: "skills",
    kind: "language",
    title: "Languages",
    hint: "Languages you speak or write.",
    addLabel: "Add language",
    titleLabel: "Language",
    titlePlaceholder: "e.g. Hindi",
    orgLabel: "Level",
    orgSelect: PROFICIENCY_OPTIONS,
    dates: "none",
    hideDescription: true,
    compact: true,
  },
];

const emptyEntry = (kind) => ({
  kind,
  title: "",
  organization: "",
  location: "",
  startDate: "",
  endDate: "",
  description: "",
  link: "",
});

const withMemory = (profile = {}) => ({
  ...profile,
  memory: Array.isArray(profile.memory) ? profile.memory : [],
});

function MonthYearSelect({
  id,
  label,
  value,
  onChange,
  disabled = false,
  optional = false,
}) {
  const { month, year } = parseMonthYear(value);
  const set = (nextMonth, nextYear) =>
    onChange(formatMonthYear(nextMonth, nextYear));
  return (
    <div className="col-6 col-md-3">
      <label className="form-label small fw-medium" htmlFor={`${id}-month`}>
        {label}
      </label>
      <div className="d-flex gap-1 gap-sm-2">
        <select
          id={`${id}-month`}
          className="form-select form-select-sm"
          value={month}
          disabled={disabled}
          onChange={(e) => set(e.target.value, year || String(THIS_YEAR))}
          aria-label={`${label} month`}
        >
          <option value="">Mo</option>
          {MONTHS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          id={`${id}-year`}
          className="form-select form-select-sm"
          value={year}
          disabled={disabled}
          onChange={(e) => set(month, e.target.value)}
          aria-label={`${label} year`}
        >
          <option value="">{optional ? "Yr" : "Yr"}</option>
          {YEARS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function entrySummary(cfg, entry) {
  const when = [
    entry.startDate,
    isPresent(entry.endDate) ? "Present" : entry.endDate,
  ]
    .filter(Boolean)
    .join(" – ");
  return [entry.organization, entry.location, when].filter(Boolean).join(" · ");
}

const emptyAnswer = () => ({
  id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label: "",
  answer: "",
  isNew: true,
});

const answerSnapshot = (list) =>
  JSON.stringify(
    (list || []).map((a) => ({
      id: a.isNew ? null : a.id,
      label: a.label || "",
      answer: a.answer || "",
    })),
  );

// Answers given on earlier applications (or added here). Editable like the other memory sections;
// saved on its own (separate from profile.memory) because it lives in SavedAnswer.
function SavedAnswers({ onCountChange }) {
  const { toast } = useApp();
  const [baseline, setBaseline] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    usersApi
      .savedAnswers()
      .then((data) => {
        const list = (data.answers || []).map((a) => ({
          id: a.id,
          label: a.label || "",
          answer: a.choices?.length ? a.choices.join(", ") : a.answer || "",
          isNew: false,
        }));
        setBaseline(list);
        setDrafts(list);
        onCountChange?.(list.length);
      })
      .catch(() => {
        setBaseline([]);
        setDrafts([]);
        onCountChange?.(0);
      });
  }, [onCountChange]);

  useEffect(() => {
    if (baseline === null) return;
    onCountChange?.(drafts.length);
  }, [drafts, baseline, onCountChange]);

  const dirty =
    baseline !== null && answerSnapshot(drafts) !== answerSnapshot(baseline);

  const setDraft = (id, patch) =>
    setDrafts((list) =>
      list.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    );

  const removeDraft = (id) =>
    setDrafts((list) => list.filter((a) => a.id !== id));

  const discard = () => setDrafts(baseline || []);

  const save = async () => {
    setBusy(true);
    try {
      const keptIds = new Set();
      const next = [];
      for (const draft of drafts) {
        const label = draft.label.trim();
        const answer = draft.answer.trim();
        if (!label && !answer) continue;
        if (!label || !answer) {
          throw new Error(
            "Each remembered answer needs both a question and an answer",
          );
        }
        if (draft.isNew) {
          const { answer: saved } = await usersApi.rememberAnswer(
            label,
            answer,
          );
          next.push({
            id: saved.id,
            label: saved.label,
            answer: saved.answer,
            isNew: false,
          });
          keptIds.add(saved.id);
        } else {
          const original = baseline.find((b) => b.id === draft.id);
          if (
            original &&
            original.label === label &&
            original.answer === answer
          ) {
            next.push({ ...draft, isNew: false });
            keptIds.add(draft.id);
          } else {
            const { answer: saved } = await usersApi.updateAnswer(
              draft.id,
              label,
              answer,
            );
            next.push({
              id: saved.id,
              label: saved.label,
              answer: saved.answer,
              isNew: false,
            });
            keptIds.add(saved.id);
          }
        }
      }
      for (const prev of baseline || []) {
        if (!keptIds.has(prev.id)) await usersApi.forgetAnswer(prev.id);
      }
      setBaseline(next);
      setDrafts(next);
      toast("Remembered answers saved");
    } catch (err) {
      toast(err.message, "danger");
    }
    setBusy(false);
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h2 className="h6 mb-0">Remembered answers</h2>
        <p className="small text-muted">
          Answers from a job's Apply tab land here. Add your own too - a fixed
          answer, or a short template Platypus can adapt (e.g. “Why do you want
          to work here?”).
        </p>

        {baseline === null ? (
          <div className="small text-muted">Loading…</div>
        ) : (
          <>
            {drafts.length === 0 && (
              <div className="small text-muted mb-3">None added yet.</div>
            )}
            <div className="memory-entry-list">
              {drafts.map((a, i) => (
                <div key={a.id} className="memory-entry memory-entry-open">
                  <div className="row g-2">
                    <div className="col-12">
                      <label
                        className="form-label small fw-medium"
                        htmlFor={`ra-q-${i}`}
                      >
                        Question
                      </label>
                      <input
                        id={`ra-q-${i}`}
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="e.g. Why do you want to work here?"
                        value={a.label}
                        onChange={(e) =>
                          setDraft(a.id, { label: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label
                        className="form-label small fw-medium"
                        htmlFor={`ra-a-${i}`}
                      >
                        Answer or template
                      </label>
                      <textarea
                        id={`ra-a-${i}`}
                        className="form-control form-control-sm"
                        rows={2}
                        placeholder="Fixed answer, or a short template Platypus can adapt."
                        value={a.answer}
                        onChange={(e) =>
                          setDraft(a.id, { answer: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    {!a.label.trim() || !a.answer.trim() ? (
                      <span className="small text-muted">
                        Needs a question and an answer.
                      </span>
                    ) : (
                      <span />
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger text-decoration-none"
                      onClick={() => removeDraft(a.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setDrafts((list) => [...list, emptyAnswer()])}
            >
              <i className="bi bi-plus-lg me-1" /> Add remembered answer
            </button>
          </>
        )}
      </div>

      <div className={`save-bar ${dirty ? "show" : ""}`}>
        <span className="small">You have unsaved remembered answers</span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={discard}
            disabled={busy}
          >
            Discard
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={save}
            disabled={busy}
          >
            {busy ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : (
              "Save answers"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function CompactSkillEntry({ cfg, entry, index, onChange, onRemove }) {
  return (
    <div className="memory-skill-row">
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder={cfg.titlePlaceholder}
        aria-label={cfg.titleLabel}
        value={entry.title ?? ""}
        onChange={(e) => onChange(index, { title: e.target.value })}
      />
      <select
        className="form-select form-select-sm"
        aria-label={cfg.orgLabel}
        value={entry.organization ?? ""}
        onChange={(e) => onChange(index, { organization: e.target.value })}
      >
        <option value="">Level</option>
        {cfg.orgSelect.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-sm btn-link text-muted px-1"
        onClick={() => onRemove(index)}
        title="Remove"
        aria-label={`Remove ${entry.title || cfg.titleLabel}`}
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
}

function MemoryEntry({ cfg, entry, index, onChange, onRemove }) {
  const current = Boolean(cfg.currentLabel) && isPresent(entry.endDate);
  const degreeParts = cfg.titleSelect ? splitDegreeTitle(entry.title) : null;
  const [open, setOpen] = useState(!String(entry.title || "").trim());

  if (cfg.compact) {
    return (
      <CompactSkillEntry
        cfg={cfg}
        entry={entry}
        index={index}
        onChange={onChange}
        onRemove={onRemove}
      />
    );
  }

  const field = (key, label, props = {}) => (
    <div className={props.colClass || "col-md-6"} key={key}>
      <label
        className="form-label small fw-medium"
        htmlFor={`m-${index}-${key}`}
      >
        {label}
      </label>
      <input
        id={`m-${index}-${key}`}
        type="text"
        className="form-control form-control-sm"
        placeholder={props.placeholder}
        value={entry[key] ?? ""}
        onChange={(e) => onChange(index, { [key]: e.target.value })}
      />
    </div>
  );

  const setDegree = (patch) => {
    const next = { ...degreeParts, ...patch };
    onChange(index, {
      title: joinDegreeTitle(next.degree, next.field, next.other),
    });
  };

  const summary = entrySummary(cfg, entry);

  if (!open) {
    return (
      <div className="memory-entry memory-entry-collapsed">
        <button
          type="button"
          className="memory-entry-summary"
          onClick={() => setOpen(true)}
        >
          <span className="memory-entry-summary-title">
            {entry.title ||
              `Untitled ${cfg.title.replace(/s$/, "").toLowerCase()}`}
          </span>
          {summary && (
            <span className="memory-entry-summary-meta">{summary}</span>
          )}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-link text-muted px-2"
          onClick={() => onRemove(index)}
          title="Remove"
          aria-label="Remove"
        >
          <i className="bi bi-trash" />
        </button>
      </div>
    );
  }

  return (
    <div className="memory-entry memory-entry-open">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="small text-muted fw-medium">
          {cfg.title.replace(/s$/, "")}
        </span>
        <div className="d-flex gap-1">
          {entry.title?.trim() && (
            <button
              type="button"
              className="btn btn-sm btn-link text-muted text-decoration-none"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-link text-danger text-decoration-none"
            onClick={() => onRemove(index)}
          >
            Remove
          </button>
        </div>
      </div>
      <div className="row g-2">
        {cfg.titleSelect ? (
          <>
            <div className="col-md-6">
              <label
                className="form-label small fw-medium"
                htmlFor={`m-${index}-degree`}
              >
                {cfg.titleLabel}
              </label>
              <select
                id={`m-${index}-degree`}
                className="form-select form-select-sm"
                value={degreeParts.degree}
                onChange={(e) =>
                  setDegree({
                    degree: e.target.value,
                    field: e.target.value === "Other" ? "" : degreeParts.field,
                    other: "",
                  })
                }
              >
                <option value="">Select a degree</option>
                {cfg.titleSelect.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            {degreeParts.degree === "Other" ? (
              <div className="col-md-6">
                <label
                  className="form-label small fw-medium"
                  htmlFor={`m-${index}-other`}
                >
                  Describe the qualification
                </label>
                <input
                  id={`m-${index}-other`}
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="e.g. Diploma in Data Analytics"
                  value={degreeParts.other}
                  onChange={(e) => setDegree({ other: e.target.value })}
                />
              </div>
            ) : cfg.fieldOfStudy ? (
              <div className="col-md-6">
                <label
                  className="form-label small fw-medium"
                  htmlFor={`m-${index}-field`}
                >
                  Field of study (optional)
                </label>
                <input
                  id={`m-${index}-field`}
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="e.g. Computer Science"
                  value={degreeParts.field}
                  onChange={(e) => setDegree({ field: e.target.value })}
                />
              </div>
            ) : null}
          </>
        ) : (
          field("title", cfg.titleLabel, { placeholder: cfg.titlePlaceholder })
        )}

        {cfg.orgSelect ? (
          <div className="col-md-6">
            <label
              className="form-label small fw-medium"
              htmlFor={`m-${index}-org`}
            >
              {cfg.orgLabel}
            </label>
            <select
              id={`m-${index}-org`}
              className="form-select form-select-sm"
              value={entry.organization ?? ""}
              onChange={(e) =>
                onChange(index, { organization: e.target.value })
              }
            >
              <option value="">Ask me / not set</option>
              {cfg.orgSelect.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        ) : (
          field("organization", cfg.orgLabel, {
            placeholder: cfg.orgPlaceholder,
          })
        )}

        {cfg.showLocation &&
          field("location", "Location", {
            placeholder: "e.g. Buffalo, NY or Remote",
            colClass: "col-12 col-md-6",
          })}

        {cfg.dateMode === "monthYear" && cfg.dates !== "none" && (
          <>
            <MonthYearSelect
              id={`m-${index}-start`}
              label={cfg.startLabel}
              value={entry.startDate}
              onChange={(v) => onChange(index, { startDate: v })}
              optional
            />
            {cfg.dates === "range" && (
              <MonthYearSelect
                id={`m-${index}-end`}
                label={cfg.endLabel}
                value={current ? "" : entry.endDate}
                onChange={(v) => onChange(index, { endDate: v })}
                disabled={current}
                optional
              />
            )}
            {cfg.currentLabel && (
              <div className="col-12">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`m-${index}-current`}
                    checked={current}
                    onChange={(e) =>
                      onChange(index, {
                        endDate: e.target.checked ? "Present" : "",
                      })
                    }
                  />
                  <label
                    className="form-check-label small"
                    htmlFor={`m-${index}-current`}
                  >
                    {cfg.currentLabel}
                  </label>
                </div>
              </div>
            )}
          </>
        )}

        {cfg.showLink &&
          field("link", "Link (optional)", {
            placeholder: "https://",
            colClass: "col-12 col-md-6",
          })}

        {!cfg.hideDescription && (
          <div className="col-12">
            <label
              className="form-label small fw-medium"
              htmlFor={`m-${index}-description`}
            >
              What should Platypus know about it?
            </label>
            <textarea
              id={`m-${index}-description`}
              className="form-control form-control-sm"
              rows={2}
              placeholder="What you did, tools you used, and what came of it."
              value={entry.description ?? ""}
              onChange={(e) => onChange(index, { description: e.target.value })}
            />
          </div>
        )}
      </div>
      {!entry.title?.trim() && (
        <div className="small text-muted mt-2">
          Give it a title - entries without one aren't saved.
        </div>
      )}
    </div>
  );
}

const kindTitle = (kind) =>
  MEMORY_KINDS.find((k) => k.kind === kind)?.title.replace(/s$/, "") || kind;

// Chat proposal card: preview, optional edit, then Remember or Dismiss.
function ChatProposalCard({ proposal, onAccept, onDismiss }) {
  const [editing, setEditing] = useState(false);
  const [entry, setEntry] = useState(() => ({ ...emptyEntry(proposal.entry.kind), ...proposal.entry }));
  const cfg =
    MEMORY_KINDS.find((k) => k.kind === entry.kind) || MEMORY_KINDS[0];
  const patch = (p) => setEntry((e) => ({ ...e, ...p }));
  const current = isPresent(entry.endDate);

  return (
    <div className="border rounded p-3 mt-3 memory-proposal">
      <div className="small fw-medium mb-1">
        Platypus wants to {proposal.action === "update" ? "update" : "remember"}
        : {kindTitle(entry.kind)}
        {entry.title ? ` - ${entry.title}` : ""}
        {entry.organization ? ` · ${entry.organization}` : ""}
      </div>
      {proposal.reason && !editing && (
        <div className="small text-muted mb-2">{proposal.reason}</div>
      )}

      {editing ? (
        <div className="row g-2 mb-2">
          <div className="col-12 col-md-4">
            <label className="form-label small fw-medium">Type</label>
            <select
              className="form-select form-select-sm"
              value={entry.kind}
              onChange={(e) => patch({ kind: e.target.value })}
            >
              {MEMORY_KINDS.map((k) => (
                <option key={k.kind} value={k.kind}>
                  {kindTitle(k.kind)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-8">
            <label className="form-label small fw-medium">
              {cfg.titleLabel || "Title"}
            </label>
            {cfg.titleSelect ? (
              <select
                className="form-select form-select-sm"
                value={entry.title}
                onChange={(e) => patch({ title: e.target.value })}
              >
                {cfg.titleSelect.map((o) => (
                  <option key={o || "blank"} value={o}>
                    {o || "Select…"}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="form-control form-control-sm"
                value={entry.title}
                placeholder={cfg.titlePlaceholder}
                onChange={(e) => patch({ title: e.target.value })}
              />
            )}
          </div>
          {cfg.orgLabel && (
            <div className="col-12 col-md-6">
              <label className="form-label small fw-medium">{cfg.orgLabel}</label>
              {cfg.orgSelect ? (
                <select
                  className="form-select form-select-sm"
                  value={entry.organization}
                  onChange={(e) => patch({ organization: e.target.value })}
                >
                  {cfg.orgSelect.map((o) => (
                    <option key={o || "blank"} value={o}>
                      {o || "Select…"}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={entry.organization}
                  placeholder={cfg.orgPlaceholder}
                  onChange={(e) => patch({ organization: e.target.value })}
                />
              )}
            </div>
          )}
          {cfg.showLocation && (
            <div className="col-12 col-md-6">
              <label className="form-label small fw-medium">Location</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={entry.location}
                placeholder="City or Remote"
                onChange={(e) => patch({ location: e.target.value })}
              />
            </div>
          )}
          {cfg.dates === "range" && (
            <>
              <MonthYearSelect
                id={`prop-${proposal.action}-start`}
                label={cfg.startLabel || "Start"}
                value={entry.startDate}
                onChange={(v) => patch({ startDate: v })}
              />
              <MonthYearSelect
                id={`prop-${proposal.action}-end`}
                label={cfg.endLabel || "End"}
                value={current ? "" : entry.endDate}
                onChange={(v) => patch({ endDate: v })}
                disabled={current}
                optional
              />
              {cfg.currentLabel && (
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`prop-current-${entry.kind}-${entry.title}`}
                      checked={current}
                      onChange={(e) =>
                        patch({
                          endDate: e.target.checked ? "Present" : "",
                        })
                      }
                    />
                    <label
                      className="form-check-label small"
                      htmlFor={`prop-current-${entry.kind}-${entry.title}`}
                    >
                      {cfg.currentLabel}
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
          {cfg.dates === "single" && (
            <MonthYearSelect
              id={`prop-${proposal.action}-when`}
              label={cfg.startLabel || "Date"}
              value={entry.startDate}
              onChange={(v) => patch({ startDate: v })}
              optional
            />
          )}
          {cfg.showLink && (
            <div className="col-12">
              <label className="form-label small fw-medium">Link</label>
              <input
                type="url"
                className="form-control form-control-sm"
                value={entry.link}
                placeholder="https://"
                onChange={(e) => patch({ link: e.target.value })}
              />
            </div>
          )}
          {!cfg.hideDescription && (
            <div className="col-12">
              <label className="form-label small fw-medium">Description</label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                value={entry.description}
                onChange={(e) => patch({ description: e.target.value })}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          {entry.description && (
            <div
              className="small text-muted mb-1"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {entry.description}
            </div>
          )}
          {(entry.location || entry.startDate || entry.endDate || entry.link) && (
            <div className="small text-muted mb-2">
              {[
                entry.location,
                [entry.startDate, isPresent(entry.endDate) ? "Present" : entry.endDate]
                  .filter(Boolean)
                  .join(" – "),
                entry.link,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </>
      )}

      <div className="d-flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!entry.title?.trim()}
          onClick={() => onAccept({ ...proposal, entry })}
        >
          <i className="bi bi-check-lg me-1" /> Remember this
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setEditing((v) => !v)}
        >
          <i className={`bi ${editing ? "bi-eye" : "bi-pencil"} me-1`} />
          {editing ? "Preview" : "Edit"}
        </button>
        <button type="button" className="btn btn-sm btn-light" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

function RewriteCard({ rewrite, onAccept, onDismiss }) {
  const [editing, setEditing] = useState(false);
  const [after, setAfter] = useState(rewrite.after);

  return (
    <div className="border rounded p-3 mt-3 memory-proposal">
      <div className="small fw-medium mb-1">
        Rewrite suggestion - {kindTitle(rewrite.kind)}: {rewrite.title}
      </div>
      <div className="small text-muted mb-1">
        <span className="fw-medium">Now:</span> {rewrite.before}
      </div>
      {editing ? (
        <div className="mb-2">
          <label className="form-label small fw-medium">Suggested</label>
          <textarea
            className="form-control form-control-sm"
            rows={3}
            value={after}
            onChange={(e) => setAfter(e.target.value)}
          />
        </div>
      ) : (
        <div className="small mb-2">
          <span className="fw-medium">Suggested:</span> {after}
        </div>
      )}
      <div className="d-flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!after.trim()}
          onClick={() => onAccept({ ...rewrite, after: after.trim() })}
        >
          <i className="bi bi-check-lg me-1" /> Use this wording
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setEditing((v) => !v)}
        >
          <i className={`bi ${editing ? "bi-eye" : "bi-pencil"} me-1`} />
          {editing ? "Preview" : "Edit"}
        </button>
        <button type="button" className="btn btn-sm btn-light" onClick={onDismiss}>
          Keep mine
        </button>
      </div>
    </div>
  );
}

function Memory() {
  const { profile, updateProfile, toast, documents, integrations, user } =
    useApp();
  const { setUser } = useAuth();
  const [form, setForm] = useState(() => withMemory(profile));
  const [busy, setBusy] = useState(false);
  const [chatLog, setChatLog] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [rewrites, setRewrites] = useState([]);
  const [rewriteBusy, setRewriteBusy] = useState(false);
  const [filling, setFilling] = useState(false);
  const [tab, setTab] = useState("basics");
  const [answerCount, setAnswerCount] = useState(0);
  const autofillTried = useRef(false);

  useEffect(() => setForm(withMemory(profile)), [profile]);

  useEffect(() => {
    usersApi
      .savedAnswers()
      .then((data) => setAnswerCount((data.answers || []).length))
      .catch(() => setAnswerCount(0));
  }, []);

  const entries = form.memory;
  const setEntry = (index, patch) =>
    setForm({
      ...form,
      memory: entries.map((e, i) => (i === index ? { ...e, ...patch } : e)),
    });
  const addEntry = (kind) =>
    setForm({ ...form, memory: [...entries, emptyEntry(kind)] });
  const removeEntry = (index) =>
    setForm({ ...form, memory: entries.filter((_, i) => i !== index) });

  const dirty = JSON.stringify(form) !== JSON.stringify(withMemory(profile));

  const tabCounts = {
    career: entries.filter((e) =>
      ["experience", "education", "project"].includes(e.kind),
    ).length,
    highlights: entries.filter((e) =>
      ["award", "publication", "patent", "certification"].includes(e.kind),
    ).length,
    skills: entries.filter((e) => e.kind === "skill" || e.kind === "language")
      .length,
    answers: answerCount,
  };

  const hasResume = (documents.resumes || []).length > 0;
  const canUseAi = Boolean(integrations.ai);

  // Learns memory entries from the uploaded resume. Adds only what's missing; never overwrites.
  const fillFromResume = async (quiet = false) => {
    setFilling(true);
    try {
      const data = await usersApi.memoryFromResume();
      if (data.user) setUser(data.user);
      if (!quiet || data.added > 0) {
        toast(
          data.added > 0
            ? `Added ${data.added} ${data.added === 1 ? "thing" : "things"} from your resume (free - no AI credits)`
            : "Read your resume - everything in it is already in memory (and blank fields like location were filled)",
        );
      }
      return data;
    } catch (err) {
      if (!quiet) toast(err.message, "danger");
      return null;
    } finally {
      setFilling(false);
    }
  };

  // Already have a resume but an empty memory? Fill it in for free (no AI) once per session.
  useEffect(() => {
    if (autofillTried.current) return;
    if (entries.length > 0 || !hasResume || !user?.id) return;
    const key = `platypus-memory-autofill-${user.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    autofillTried.current = true;
    fillFromResume(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length, hasResume, user?.id]);

  const sendChat = async () => {
    const message = chatInput.trim();
    if (!message || chatBusy) return;
    setChatInput("");
    setChatLog((log) => [...log, { from: "me", text: message }]);
    setChatBusy(true);
    try {
      const { reply, proposals: fresh } = await usersApi.memoryChat(message);
      setChatLog((log) => [
        ...log,
        { from: "platypus", text: reply || "Got it." },
      ]);
      if (fresh?.length) {
        setProposals((list) => [
          ...list,
          ...fresh.map((p) => ({
            ...p,
            _uid: `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          })),
        ]);
      }
    } catch (err) {
      setChatLog((log) => [
        ...log,
        { from: "platypus", text: `Sorry - ${err.message}` },
      ]);
    }
    setChatBusy(false);
  };

  // Applies one approved chat proposal and saves straight away, so Platypus really has learned it.
  const applyProposal = async (proposal) => {
    const matches = (e) => String(e._id || e.id) === String(proposal.entryId);
    const memory =
      proposal.action === "update" && entries.some(matches)
        ? entries.map((e) => (matches(e) ? { ...e, ...proposal.entry } : e))
        : [...entries, proposal.entry];
    if (await updateProfile({ ...form, memory })) {
      setProposals((list) => list.filter((p) => p._uid !== proposal._uid));
      toast("Saved to Platypus's memory");
    }
  };

  const getRewrites = async () => {
    setRewriteBusy(true);
    try {
      const { proposals: fresh } = await usersApi.memoryRewrite();
      setRewrites(fresh || []);
      if (!fresh?.length)
        toast("Platypus thinks your memory reads well already");
    } catch (err) {
      toast(err.message, "danger");
    }
    setRewriteBusy(false);
  };

  const applyRewrite = async (rewrite) => {
    const matches = (e) => String(e._id || e.id) === String(rewrite.entryId);
    if (!entries.some(matches)) {
      toast("That entry changed - ask Platypus to rewrite again", "danger");
      setRewrites((list) =>
        list.filter((r) => r.entryId !== rewrite.entryId),
      );
      return;
    }
    const memory = entries.map((e) =>
      matches(e) ? { ...e, description: rewrite.after } : e,
    );
    if (await updateProfile({ ...form, memory })) {
      setRewrites((list) =>
        list.filter((r) => r.entryId !== rewrite.entryId),
      );
      toast("Memory updated");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (await updateProfile(form)) toast("Memory saved");
    setBusy(false);
  };

  return (
    <div className="memory-page">
      <PageHeader
        title={
          <>
            Memory
            <span className="memory-info ms-2" tabIndex={0}>
              <i className="bi bi-info-circle" aria-hidden="true" />
              <span className="memory-info-tip" role="tooltip">
                <strong>Platypus's memory.</strong> Add experiences, projects,
                education, publications, awards, patents, certifications, skills
                and languages - or fill them from your resume and from what you
                tell Platypus below. The more it knows, the better it can tailor
                a resume, write a cover letter and answer application questions
                in your voice. When an application asks something it doesn't
                know yet, it asks you on that job's Apply tab; your answer is
                remembered here and never asked again.
                <br />
                <br />
                Platypus only uses facts you've added here or that are on your
                resume - it never invents employers, titles or numbers.
                Voluntary self-identification is only used to fill those form
                questions and is never sent to AI.
              </span>
            </span>
          </>
        }
        subtitle="What Platypus knows about you - for jobs, resumes, cover letters and applications."
      />

      {entries.length === 0 && hasResume && (
        <div className="memory-banner mb-3">
          <div className="flex-grow-1 min-w-0">
            <div className="fw-medium mb-1">Fill memory from your resume</div>
            <div className="small text-muted">
              Free, no AI credits. Adds what's missing - never overwrites what
              you've written.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary flex-shrink-0"
            onClick={() => fillFromResume()}
            disabled={filling}
          >
            {filling ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Reading…
              </>
            ) : (
              <>
                <PlatypusLogo size={18} /> Fill from resume
              </>
            )}
          </button>
        </div>
      )}

      <div className="memory-chat card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <PlatypusLogo size={26} />
            <h2 className="h6 mb-0 flex-grow-1">Tell Platypus</h2>
            {hasResume && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => fillFromResume()}
                disabled={filling}
                title="Free - reads your resume with no AI credits. Adds missing entries and blank fields (like location)"
              >
                {filling ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Reading…
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-text me-1" /> Fill from
                    resume
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={getRewrites}
              disabled={rewriteBusy || !canUseAi || entries.length === 0}
              title="Platypus proposes tighter, resume-ready wording for your entries - you approve each one"
            >
              {rewriteBusy ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Reading…
                </>
              ) : (
                <>
                  <i className="bi bi-pencil-square me-1" /> Rewrite my memory
                </>
              )}
            </button>
          </div>
          <p className="small text-muted">
            Tell Platypus what it should remember - “Hey Platypus, I just won
            the X award for…”, “I built a project that…”, “Add Hindi, I'm
            fluent”. It proposes the memory entry below and only saves it when
            you approve. Ask it what it remembers, too.
          </p>
          {!canUseAi ? (
            <AccessNotice feature="Talking to Platypus about your memory" />
          ) : (
            <>
              {chatLog.length > 0 && (
                <div className="mb-3">
                  {chatLog.map((m, i) => (
                    <div
                      key={i}
                      className={`d-flex mb-2 ${m.from === "me" ? "justify-content-end" : ""}`}
                    >
                      <div
                        className={`border rounded px-3 py-2 small ${m.from === "me" ? "bg-light" : ""}`}
                        style={{ maxWidth: "85%", whiteSpace: "pre-wrap" }}
                      >
                        {m.from === "platypus" && (
                          <span className="fw-medium d-block mb-1">
                            <PlatypusLogo size={16} /> Platypus
                          </span>
                        )}
                        {m.text}
                      </div>
                    </div>
                  ))}
                  {chatBusy && (
                    <div className="small text-muted">
                      <span className="spinner-border spinner-border-sm me-2" />
                      Platypus is thinking…
                    </div>
                  )}
                </div>
              )}
              <div className="d-flex gap-2">
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="e.g. Hey Platypus, I just won the HackNYU best hack award for building a transit app in React…"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary align-self-end"
                  onClick={sendChat}
                  disabled={chatBusy || !chatInput.trim()}
                >
                  <i className="bi bi-send" />
                </button>
              </div>

              {proposals.map((p) => (
                <ChatProposalCard
                  key={p._uid}
                  proposal={p}
                  onAccept={applyProposal}
                  onDismiss={() =>
                    setProposals((list) => list.filter((x) => x._uid !== p._uid))
                  }
                />
              ))}

              {rewrites.map((r) => (
                <RewriteCard
                  key={r.entryId}
                  rewrite={r}
                  onAccept={applyRewrite}
                  onDismiss={() =>
                    setRewrites((list) =>
                      list.filter((x) => x.entryId !== r.entryId),
                    )
                  }
                />
              ))}
            </>
          )}
        </div>
      </div>

      <nav className="memory-tabs" aria-label="Memory sections">
        {MEMORY_TABS.map((t) => {
          const count = tabCounts[t.id];
          return (
            <button
              key={t.id}
              type="button"
              className={`memory-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <i className={`bi ${t.icon}`} aria-hidden="true" />
              <span>{t.label}</span>
              {count > 0 && <span className="memory-tab-count">{count}</span>}
            </button>
          );
        })}
      </nav>

      <form
        onSubmit={save}
        className={tab === "answers" ? "d-none" : undefined}
      >
        {tab === "basics" &&
          SECTIONS.filter((s) => s.tab === "basics").map((section) => (
            <div key={section.title} className="memory-panel card mb-3">
              <div className="card-body">
                <h2 className="h6 mb-0">{section.title}</h2>
                <p className="small text-muted mb-3">{section.hint}</p>
                <div className="row g-2">
                  {section.fields.map((f) => (
                    <div key={f.key} className={`col-12 col-md-${f.col}`}>
                      <label
                        className="form-label small fw-medium"
                        htmlFor={`p-${f.key}`}
                      >
                        {f.label}
                      </label>
                      {f.options ? (
                        <select
                          id={`p-${f.key}`}
                          className="form-select form-select-sm"
                          value={form[f.key] ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, [f.key]: e.target.value })
                          }
                        >
                          {f.options.map((o) => (
                            <option key={o} value={o}>
                              {o || "Ask me"}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={`p-${f.key}`}
                          type={f.type || "text"}
                          className="form-control form-control-sm"
                          placeholder={f.placeholder}
                          value={form[f.key] ?? ""}
                          onChange={(e) =>
                            setForm({ ...form, [f.key]: e.target.value })
                          }
                        />
                      )}
                      {f.help && <div className="form-text">{f.help}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

        {MEMORY_KINDS.filter((cfg) => cfg.tab === tab).map((cfg) => {
          const mine = entries
            .map((entry, index) => ({ entry, index }))
            .filter(({ entry }) => entry.kind === cfg.kind);
          return (
            <div key={cfg.kind} className="memory-panel card mb-3">
              <div className="card-body">
                <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-1">
                  <h2 className="h6 mb-0">{cfg.title}</h2>
                  {mine.length > 0 && (
                    <span className="small text-muted">{mine.length}</span>
                  )}
                </div>
                <p className="small text-muted mb-3">{cfg.hint}</p>
                {mine.length === 0 && (
                  <div className="small text-muted mb-3">None added yet.</div>
                )}
                <div
                  className={
                    cfg.compact ? "memory-skill-grid" : "memory-entry-list"
                  }
                >
                  {mine.map(({ entry, index }) => (
                    <MemoryEntry
                      key={entry._id || entry.id || `${cfg.kind}-${index}`}
                      cfg={cfg}
                      entry={entry}
                      index={index}
                      onChange={setEntry}
                      onRemove={removeEntry}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => addEntry(cfg.kind)}
                >
                  <i className="bi bi-plus-lg me-1" /> {cfg.addLabel}
                </button>
              </div>
            </div>
          );
        })}

        <div className={`save-bar ${dirty ? "show" : ""}`}>
          <span className="small">You have unsaved changes</span>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => setForm(withMemory(profile))}
            >
              Discard
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-primary"
              disabled={busy}
            >
              Save memory
            </button>
          </div>
        </div>
      </form>

      {tab === "answers" && <SavedAnswers onCountChange={setAnswerCount} />}
    </div>
  );
}

export default Memory;
